import { MethodBadge } from '../common/MethodBadge';
import { StatusBadge } from '../common/StatusBadge';
import { JsonViewer } from '../request/JsonViewer';

/*
 * The landing product shot (docs/brand.md → Terminal and request log): the request log and
 * inspector, built from the real badges and JsonViewer over fixture data so it follows the
 * theme and can't drift from the app. Static markup only — no clock, no timers — so the
 * prerendered page and the hydrated one agree. The newest row's arrival is CSS
 * (`log-arrive` in index.css).
 */

export const PREVIEW_URL = 'https://my-app.mockd.sh';

interface LogLine {
  method: string;
  path: string;
  status: number;
  ms: number;
}

/** Newest first, as the request log lists them. The first row is the selected one. */
export const PREVIEW_LOG: readonly LogLine[] = [
  { method: 'POST', path: '/api/orders', status: 201, ms: 14 },
  { method: 'GET', path: '/api/users/42', status: 200, ms: 9 },
  { method: 'PATCH', path: '/api/users/42', status: 200, ms: 11 },
  { method: 'GET', path: '/api/users/7', status: 404, ms: 6 },
  { method: 'DELETE', path: '/api/sessions/current', status: 204, ms: 8 },
];

export const PREVIEW_BODY = '{"item":"sku_1042","quantity":2}';

const PREVIEW_HEADERS: readonly [string, string][] = [
  ['content-type', 'application/json'],
  ['user-agent', 'curl/8.7.1'],
];

export function ProductPreview() {
  const [selected, ...rest] = PREVIEW_LOG;

  return (
    <div
      role="img"
      aria-label={`The mockd request log for ${PREVIEW_URL}, showing ${PREVIEW_LOG.length} requests and the newest one open in the inspector`}
      className="rounded-box border border-base-300 bg-base-100 overflow-hidden text-left"
    >
      {/* Header: the endpoint URL and the live dot, as on the endpoint page. */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-base-300 bg-base-200">
        <span className="font-mono text-sm text-base-content truncate">{PREVIEW_URL}</span>
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          <span className="relative flex size-2.5">
            <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
            <span className="relative inline-flex rounded-full size-2.5 bg-success" />
          </span>
          <span className="text-xs font-medium text-success">Live</span>
        </span>
      </div>

      <div className="md:grid md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* Request log */}
        <div className="md:border-r border-base-300">
          <LogRow line={selected} isSelected className="log-arrive" />
          {rest.map((line) => (
            <LogRow key={`${line.method} ${line.path} ${line.status}`} line={line} />
          ))}
        </div>

        {/* Inspector for the selected request */}
        <div className="hidden md:block p-4 space-y-4">
          <div className="flex items-center gap-2">
            <MethodBadge method={selected.method} />
            <span className="font-mono text-sm text-base-content truncate">{selected.path}</span>
          </div>
          <div>
            <p className="text-xs font-medium text-base-content/70 mb-1.5">Headers</p>
            <dl className="font-mono text-xs space-y-1">
              {PREVIEW_HEADERS.map(([name, value]) => (
                <div key={name} className="flex gap-2">
                  <dt className="text-base-content/70">{name}</dt>
                  <dd className="text-base-content truncate">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <p className="text-xs font-medium text-base-content/70 mb-1.5">Body</p>
            <JsonViewer data={PREVIEW_BODY} />
          </div>
        </div>
      </div>
    </div>
  );
}

/*
 * Selection is the primary left rule alone. The badges reach AA only on `base-100`, and a
 * tinted row (the app's `bg-primary/10`) drops light-theme POST/2xx below 4.5:1.
 */
function LogRow({ line, isSelected, className = '' }: { line: LogLine; isSelected?: boolean; className?: string }) {
  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 border-b border-base-200 last:border-b-0 border-l-2 ${
        isSelected ? 'border-l-primary' : 'border-l-transparent'
      } ${className}`}
    >
      <MethodBadge method={line.method} />
      <StatusBadge status={line.status} />
      <span className="flex-1 font-mono text-sm text-base-content truncate">{line.path}</span>
      <span className="font-mono text-xs text-base-content/70">{line.ms}ms</span>
    </div>
  );
}
